import React, { useState, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Layout from "layouts/LayoutMasterDetail";

import { StoreContext } from "shared/context/store-context";
import { ownerShipTypes, yesNoUnknowTypes, plateTypes, vehiclePackageTypes } from "shared/util/types";
import { formatDate } from "shared/util/time";
import { PERMISSION_TYPES } from "@routebot/constants";
import { AuthContext } from "shared/context/auth-context";

import InventoryVehicleItem from "./InventoryVehicleItem";

const InventoryVehicles = ({ ownershipFilter }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const itemType = "vehicle";
  const listName =
    ownershipFilter === "owned" ? "owned_vehicles" : ownershipFilter === "rented" ? "rented_vehicles" : "main_vehicles";

  const { vehicles, DeleteVehicle, UpdateVehicle, InsertVehicle, driverOptions, attendantOptions } =
    useContext(StoreContext);

  const [pageItems, setPageItems] = useState();

  // Aynı sürücünün kaç araçta atandığını hesapla (tüm araçlar üzerinden; Özmal/Kiralık ayrı sekmeler olduğu için)
  const driverVehicleCountMap = useMemo(() => {
    const map = {};
    if (!vehicles?.length) return map;
    vehicles.forEach((v) => {
      const id = v.driver?.toString?.() || v.driver;
      if (id) {
        // İsmi sadece "-" olan sürücüleri (boş sürücü) ihmal et
        const driverLabel = driverOptions?.find((d) => d.value?.toString?.() === id)?.label;
        if (driverLabel && driverLabel.trim() === "-") return;
        map[id] = (map[id] || 0) + 1;
      }
    });
    return map;
  }, [vehicles, driverOptions]);

  const shouldHighlightDate = (date) => {
    if (!date) {
      return true;
    }
    const currentDate = new Date();
    const oneMonthAfter = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
    return date <= formatDate(oneMonthAfter).split(" ")[0];
  };

  useEffect(() => {
    if (!vehicles) {
      setPageItems([]);
      return;
    }

    if (ownershipFilter === "owned") {
      // Özmal araçlar - sadece owned olanlar
      setPageItems(vehicles.filter((vehicle) => vehicle.ownership === "owned"));
    } else if (ownershipFilter === "rented") {
      // Kiralık araçlar - owned olmayanlar (rentedWithDriver, rentedWithoutDriver, vb.)
      setPageItems(vehicles.filter((vehicle) => vehicle.ownership !== "owned"));
    } else {
      // Filtre yoksa tümünü göster
      setPageItems(vehicles);
    }
  }, [vehicles, ownershipFilter]);

  const showArventoTelemetry =
    auth.permissions?.[PERMISSION_TYPES.ACCESS_TO_TELEMETRY] && auth.company?.arventoConfig?.enabled;

  const isTrackingNodeMismatch = (row) => {
    const serial = String(row?.trackingSerialNo || "").trim();
    const node = String(row?.arventoNode || "").trim();
    return !serial || !node || serial !== node;
  };

  const listColumns = [
    { field: "plate", headerName: t("inventoryVehicle.columns.plate"), width: 120 },
    {
      field: "isActive",
      headerName: t("inventoryVehicle.columns.isActive"),
      width: 100,
      valueGetter: (params) => (params?.row?.isActive ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
    },
    {
      field: "plateType",
      headerName: t("inventoryVehicle.columns.plateType"),
      width: 150,
      valueGetter: (params) => {
        const type = plateTypes.find((type) => type?.value === params?.row?.plateType);
        return type ? t(`inventoryVehicle.plateTypes.${type.value}`) : "";
      },
    },
    {
      field: "vehiclePackage",
      headerName: t("inventoryVehicle.columns.vehiclePackage"),
      width: 150,
      valueGetter: (params) => {
        const type = vehiclePackageTypes.find((type) => type?.value === params?.row?.vehiclePackage);
        return type ? t(`inventoryVehicle.vehiclePackageTypes.${type.value}`) : "";
      },
    },
    {
      field: "ownership",
      headerName: t("inventoryVehicle.columns.ownership"),
      width: 150,
      valueGetter: (params) => {
        const type = ownerShipTypes.find((type) => type?.value === params?.row?.ownership);
        return type ? t(`inventoryVehicle.ownership.${type.value}`) : "";
      },
    },
    { field: "department", headerName: t("inventoryVehicle.columns.department"), width: 200 },
    {
      field: "driver",
      headerName: t("inventoryVehicle.columns.driver"),
      width: 280,
      valueGetter: (params) => {
        try {
          return (
            driverOptions.find((item) => item?.value?.toString() === params?.row?.driver?.toString())?.label || "-"
          );
        } catch {
          return "";
        }
      },
      renderCell: (params) => {
        const driverId = params?.row?.driver?.toString?.() || params?.row?.driver;
        const count = driverId ? driverVehicleCountMap[driverId] : 0;
        const label = params.value || "-";
        const isMultiple = count > 1;
        return (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span>{label}</span>
            {isMultiple && (
              <span
                title={t("inventoryVehicle.driverAssignedToMultiple", { count })}
                style={{
                  fontSize: 11,
                  color: "#b45309",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                ⚠ {t("inventoryVehicle.driverAssignedToMultiple", { count })}
              </span>
            )}
          </span>
        );
      },
      cellClassName: (params) => {
        const driverId = params?.row?.driver?.toString?.() || params?.row?.driver;
        const count = driverId ? driverVehicleCountMap[driverId] : 0;
        return count > 1 ? "cell-warning" : undefined;
      },
    },
    {
      field: "attendant",
      headerName: t("inventoryVehicle.columns.attendant"),
      width: 250,
      valueGetter: (params) => {
        return (
          attendantOptions.find((item) => item?.value?.toString() === params?.row?.attendant?.toString())?.label || "-"
        );
      },
    },
    {
      field: "generalInspectionEndDate",
      headerName: t("inventoryVehicle.columns.generalInspectionEndDate"),
      width: 150,
      cellClassName: (params) =>
        shouldHighlightDate(params?.row?.generalInspectionEndDate) ? "highlight-red" : undefined,
    },

    {
      field: "egzozInspectionEndDate",
      headerName: t("inventoryVehicle.columns.egzozInspectionEndDate"),
      width: 150,
      cellClassName: (params) =>
        shouldHighlightDate(params?.row?.egzozInspectionEndDate) ? "highlight-red" : undefined,
    },
    {
      field: "trafficPolicyEndDate",
      headerName: t("inventoryVehicle.columns.trafficPolicyEndDate"),
      width: 150,
      cellClassName: (params) => (shouldHighlightDate(params?.row?.trafficPolicyEndDate) ? "highlight-red" : undefined),
    },
    {
      field: "insuranceEndDate",
      headerName: t("inventoryVehicle.columns.insuranceEndDate"),
      width: 150,
      cellClassName: (params) => (shouldHighlightDate(params?.row?.insuranceEndDate) ? "highlight-red" : undefined),
    },
    {
      field: "chairPolicyEndDate",
      headerName: t("inventoryVehicle.columns.chairPolicyEndDate"),
      width: 150,
      cellClassName: (params) => (shouldHighlightDate(params?.row?.chairPolicyEndDate) ? "highlight-red" : undefined),
    },
    {
      field: "roadDocumentC1Date",
      headerName: t("inventoryVehicle.columns.roadDocumentC1Date"),
      width: 150,
      cellClassName: (params) => (shouldHighlightDate(params?.row?.roadDocumentC1Date) ? "highlight-red" : undefined),
    },
    {
      field: "roadDocumentC2Date",
      headerName: t("inventoryVehicle.columns.roadDocumentC2Date"),
      width: 150,
      cellClassName: (params) => (shouldHighlightDate(params?.row?.roadDocumentC2Date) ? "highlight-red" : undefined),
    },
    {
      field: "d2Date",
      headerName: t("inventoryVehicle.columns.d2Date"),
      width: 150,
      cellClassName: (params) => (shouldHighlightDate(params?.row?.d2Date) ? "highlight-red" : undefined),
    },
    {
      field: "valilikDenetimKartiDate",
      headerName: t("inventoryVehicle.columns.valilikDenetimKartiDate"),
      width: 150,
      cellClassName: (params) =>
        shouldHighlightDate(params?.row?.valilikDenetimKartiDate) ? "highlight-red" : undefined,
    },
    {
      field: "tursabLevhasiDate",
      headerName: t("inventoryVehicle.columns.tursabLevhasiDate"),
      width: 150,
      cellClassName: (params) => (shouldHighlightDate(params?.row?.tursabLevhasiDate) ? "highlight-red" : undefined),
    },
    {
      field: "generalInspectionPhoto",
      headerName: t("inventoryVehicle.columns.generalInspectionPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.generalInspectionPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.generalInspectionPhoto ? undefined : "highlight-red"),
    },
    {
      field: "egzozInspectionPhoto",
      headerName: t("inventoryVehicle.columns.egzozInspectionPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.egzozInspectionPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.egzozInspectionPhoto ? undefined : "highlight-red"),
    },
    {
      field: "trafficPolicyPhoto",
      headerName: t("inventoryVehicle.columns.trafficPolicyPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.trafficPolicyPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.trafficPolicyPhoto ? undefined : "highlight-red"),
    },
    {
      field: "insurancePhoto",
      headerName: t("inventoryVehicle.columns.insurancePhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.insurancePhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.insurancePhoto ? undefined : "highlight-red"),
    },
    {
      field: "chairPolicyPhoto",
      headerName: t("inventoryVehicle.columns.chairPolicyPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.chairPolicyPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.chairPolicyPhoto ? undefined : "highlight-red"),
    },
    {
      field: "roadDocumentC1Photo",
      headerName: t("inventoryVehicle.columns.roadDocumentC1Photo"),
      width: 150,
      valueGetter: (params) => (params?.row?.roadDocumentC1Photo ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.roadDocumentC1Photo ? undefined : "highlight-red"),
    },
    {
      field: "roadDocumentC2Photo",
      headerName: t("inventoryVehicle.columns.roadDocumentC2Photo"),
      width: 150,
      valueGetter: (params) => (params?.row?.roadDocumentC2Photo ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.roadDocumentC2Photo ? undefined : "highlight-red"),
    },
    {
      field: "d2Photo",
      headerName: t("inventoryVehicle.columns.d2Photo"),
      width: 150,
      valueGetter: (params) => (params?.row?.d2Photo ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.d2Photo ? undefined : "highlight-red"),
    },
    {
      field: "valilikDenetimKartiPhoto",
      headerName: t("inventoryVehicle.columns.valilikDenetimKartiPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.valilikDenetimKartiPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.valilikDenetimKartiPhoto ? undefined : "highlight-red"),
    },
    {
      field: "tursabLevhasiPhoto",
      headerName: t("inventoryVehicle.columns.tursabLevhasiPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.tursabLevhasiPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.tursabLevhasiPhoto ? undefined : "highlight-red"),
    },
    {
      field: "startPhoto",
      headerName: t("inventoryVehicle.columns.startPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.startPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.startPhoto ? undefined : "highlight-red"),
    },

    { field: "marka", headerName: t("inventoryVehicle.columns.marka"), width: 150 },
    { field: "model", headerName: t("inventoryVehicle.columns.model"), width: 150 },
    { field: "modelYear", headerName: t("inventoryVehicle.columns.modelYear"), width: 150 },
    { field: "formalCapacity", headerName: t("inventoryVehicle.columns.formalCapacity"), width: 150 },
    { field: "realCapacity", headerName: t("inventoryVehicle.columns.realCapacity"), width: 150 },

    { field: "owner", headerName: t("inventoryVehicle.columns.owner"), width: 150 },
    { field: "contact1", headerName: t("inventoryVehicle.columns.contact1"), width: 150 },
    { field: "address", headerName: t("inventoryVehicle.columns.address"), width: 150 },
    { field: "yakitOwner", headerName: t("inventoryVehicle.columns.yakitOwner"), width: 150 },
    {
      field: "spaceKey",
      headerName: t("inventoryVehicle.columns.spaceKey"),
      width: 150,
      valueGetter: (params) => {
        const type = yesNoUnknowTypes.find((type) => type?.value === params?.row?.spaceKey);
        return type ? t(`inventoryVehicle.spareKey.${type.value}`) : "";
      },
    },
    { field: "ruhsatNo", headerName: t("inventoryVehicle.columns.ruhsatNo"), width: 150 },
    {
      field: "trackingSerialNo",
      headerName: t("inventoryVehicle.columns.trackingSerialNo"),
      width: 150,
      cellClassName: (params) =>
        showArventoTelemetry && isTrackingNodeMismatch(params?.row) ? "highlight-red" : undefined,
    },
    ...(showArventoTelemetry
      ? [
          {
            field: "arventoNode",
            headerName: t("telemetryArventoNode"),
            width: 160,
            valueGetter: (params) => params?.row?.arventoNode || "—",
          },
        ]
      : []),
    { field: "uttsSerialNo", headerName: t("inventoryVehicle.columns.uttsSerialNo"), width: 150 },
    { field: "chassisNo", headerName: t("inventoryVehicle.columns.chassisNo"), width: 150 },

    { field: "generalInspectionDate", headerName: t("inventoryVehicle.columns.generalInspectionDate"), width: 150 },
    { field: "generalInspectionAmount", headerName: t("inventoryVehicle.columns.generalInspectionAmount"), width: 150 },
    { field: "egzozInspectionDate", headerName: t("inventoryVehicle.columns.egzozInspectionDate"), width: 150 },
    { field: "egzozInspectionAmount", headerName: t("inventoryVehicle.columns.egzozInspectionAmount"), width: 150 },
    { field: "trafficPolicyStartDate", headerName: t("inventoryVehicle.columns.trafficPolicyStartDate"), width: 150 },
    { field: "trafficPolicyAmount", headerName: t("inventoryVehicle.columns.trafficPolicyAmount"), width: 150 },
    { field: "insuranceStartDate", headerName: t("inventoryVehicle.columns.insuranceStartDate"), width: 150 },
    { field: "insuranceAmount", headerName: t("inventoryVehicle.columns.insuranceAmount"), width: 150 },
    { field: "chairPolicyStartDate", headerName: t("inventoryVehicle.columns.chairPolicyStartDate"), width: 150 },
    { field: "chairPolicyAmount", headerName: t("inventoryVehicle.columns.chairPolicyAmount"), width: 150 },

    { field: "startDate", headerName: t("inventoryVehicle.columns.startDate"), width: 150 },
    { field: "startKm", headerName: t("inventoryVehicle.columns.startKm"), width: 150 },
    {
      field: "endPhoto",
      headerName: t("inventoryVehicle.columns.endPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.endPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
    },
    { field: "endDate", headerName: t("inventoryVehicle.columns.endDate"), width: 150 },
    { field: "endKm", headerName: t("inventoryVehicle.columns.endKm"), width: 150 },

    { field: "notes", headerName: t("inventoryVehicle.columns.notes"), width: 150 },

    // {
    //   field: "files",
    //   headerName: "Dosyalar",
    //   width: 200,
    //   valueGetter: (params) => params?.row?.folder?.id || "",
    // },
  ];

  const standardHelpItems = [
    t("inventoryVehicle.help.standard1"),
    t("inventoryVehicle.help.standard2"),
    t("inventoryVehicle.help.standard3"),
    t("inventoryVehicle.help.standard4"),
  ];

  const highlightedHelpItems = [
    t("inventoryVehicle.help.highlighted1"),
    t("inventoryVehicle.help.highlighted2"),
    t("inventoryVehicle.help.highlighted3"),
    t("inventoryVehicle.help.highlighted4"),
    t("inventoryVehicle.help.highlighted5"),
    t("inventoryVehicle.help.highlighted6"),
  ];

  return (
    <Layout
      itemType={itemType}
      items={pageItems}
      listName={listName}
      listColumns={listColumns}
      isAlwaysFullScreen={true}
      hideCreateNewItem={!auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY]}
      SelectedItemComponent={InventoryVehicleItem}
      NewItemComponent={InventoryVehicleItem}
      DeleteItem={DeleteVehicle}
      InsertItem={InsertVehicle}
      UpdateItem={UpdateVehicle}
      standardHelpItems={standardHelpItems}
      highlightedHelpItems={highlightedHelpItems}
      hideCreateNewItemWhenSelected={true}
    />
  );
};

export default InventoryVehicles;
